/**
 * EditableArousalMechanicRow Component
 * 
 * A table row with inline editing capability for arousal mechanics.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Sparkles, Loader2, Activity, Brain, Users, Globe } from 'lucide-react';
import api from '../services/api';
import { to5Scale, render5Scale } from '../utils/displayConverters.jsx';

const CATEGORY_ICONS = {
    physiological: <Activity className="w-3.5 h-3.5" />,
    psychological: <Brain className="w-3.5 h-3.5" />,
    relational: <Users className="w-3.5 h-3.5" />,
    environmental: <Globe className="w-3.5 h-3.5" />
};

function EditableArousalMechanicRow({ mechanic, onUpdate, onDelete, rowIndex = 0, isDevMode = false }) {
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Track which field is being edited
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState('');
    const inputRef = useRef(null);

    // Focus input when editing starts
    useEffect(() => {
        if (editingField && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editingField]);

    // Start editing a field
    const startEditing = (field, currentValue) => {
        if (!isDevMode) return;
        setEditingField(field);
        setEditValue(currentValue?.toString() || '');
    };

    // Save field change
    const saveField = async (field) => {
        if (editValue === mechanic[field]?.toString()) {
            setEditingField(null);
            return;
        }

        setSaving(true);
        try {
            let value = editValue;
            // Convert to number for numeric fields
            if (['evidenceScore', 'intensityImpact', 'riskLevel', 'effortLevel'].includes(field)) {
                value = Math.min(10, Math.max(0, parseInt(editValue) || 0));
            }

            const updated = await api.updateArousalMechanic(mechanic.id, { [field]: value });
            onUpdate(updated);
            setEditingField(null);
        } catch (error) {
            console.error('Failed to save:', error);
        } finally {
            setSaving(false);
        }
    };

    // Handle key press in input
    const handleKeyDown = (e, field) => {
        if (e.key === 'Enter') {
            saveField(field);
        } else if (e.key === 'Escape') {
            setEditingField(null);
        }
    };

    // Delete mechanic
    const handleDelete = async () => {
        setDeleting(true);
        try {
            await api.deleteArousalMechanic(mechanic.id);
            onDelete(mechanic.id);
        } catch (error) {
            console.error('Failed to delete:', error);
            alert('Failed to delete: ' + error.message);
        } finally {
            setDeleting(false);
            setShowDeleteConfirm(false);
        }
    };

    // Score color styling - consistent with kinks
    const getScoreStyle = (score) => {
        if (score == null) return { color: '#666' };
        const intensity = score / 10;
        const r = Math.round(255 - intensity * 56);
        const g = Math.round(255 - intensity * 204);
        const b = Math.round(255 - intensity * 173);
        return {
            color: `rgb(${r}, ${g}, ${b})`,
            textShadow: intensity > 0.5 ? '0 0 8px rgba(199, 62, 82, 0.3)' : 'none'
        };
    };

    const renderNumberField = (field, value) => {
        if (editingField === field) {
            return (
                <input
                    ref={inputRef}
                    type="number"
                    min="0"
                    max="10"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={() => saveField(field)}
                    onKeyDown={(e) => handleKeyDown(e, field)}
                    className="w-10 px-1 py-0.5 bg-dark-700 border border-wine-500/50 rounded text-center text-xs text-dark-100 focus:outline-none focus:ring-1 focus:ring-wine-500"
                    disabled={saving}
                />
            );
        }

        return (
            <button
                onClick={() => startEditing(field, value)}
                className="w-8 h-5 rounded-md bg-dark-800/60 hover:bg-dark-700/80 transition-all font-mono text-xs font-medium flex items-center justify-center"
                style={getScoreStyle(value)}
            >
                {value ?? '-'}
            </button>
        );
    };

    const isEvenRow = rowIndex % 2 === 0;

    return (
        <div className={`grid grid-cols-[minmax(250px,1fr)_120px_70px_70px_70px_70px_80px] gap-2 py-2.5 px-4 transition-colors group items-center ${isEvenRow ? 'bg-dark-900/15' : 'bg-dark-900/45'
            } hover:bg-dark-800/60`}>
            {/* Name + Summary */}
            <div className="flex items-start gap-3 min-w-0">
                <div className="p-1.5 rounded bg-wine-900/20 text-wine-400">
                    {CATEGORY_ICONS[mechanic.category] || <Activity className="w-3.5 h-3.5" />}
                </div>
                <div className="min-w-0 flex-1">
                    <a href={`/mechanic/${mechanic.slug}`} className="text-dark-100 hover:text-wine-400 font-bold transition-colors truncate block text-sm">
                        {mechanic.name}
                    </a>
                    <p className="text-dark-500 text-[11px] mt-0.5 line-clamp-1 leading-relaxed">
                        {mechanic.summary}
                    </p>
                </div>
            </div>

            {/* Category */}
            <div className="text-dark-400 text-xs truncate">
                {mechanic.category}
            </div>

            {/* Evidence Score */}
            <div className="flex justify-center">
                {renderNumberField('evidenceScore', mechanic.evidenceScore)}
            </div>

            {/* Intensity Impact */}
            <div className="flex justify-center">
                {renderNumberField('intensityImpact', mechanic.intensityImpact)}
            </div>

            {/* Risk Level */}
            <div className="flex justify-center">
                {renderNumberField('riskLevel', mechanic.riskLevel)}
            </div>

            {/* Trainability */}
            <div className="flex justify-center">
                <span className={`text-[10px] font-bold uppercase ${mechanic.trainability === 'high' ? 'text-teal-400' : 'text-dark-600'
                    }`}>
                    {mechanic.trainability === 'high' ? 'High' : (mechanic.trainability || 'Low')}
                </span>
            </div>

            {/* Actions */}
            <div className="flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                {isDevMode && (
                    showDeleteConfirm ? (
                        <div className="flex gap-1">
                            <button onClick={handleDelete} disabled={deleting} className="w-5 h-5 bg-red-600/30 text-red-400 rounded text-[10px] hover:bg-red-600/40 flex items-center justify-center">
                                {deleting ? '…' : '✓'}
                            </button>
                            <button onClick={() => setShowDeleteConfirm(false)} className="w-5 h-5 text-dark-400 text-[10px] flex items-center justify-center hover:text-dark-200">
                                ✕
                            </button>
                        </div>
                    ) : (
                        <button onClick={() => setShowDeleteConfirm(true)} className="text-dark-600 hover:text-red-400 transition-colors" title="Delete">
                            🗑
                        </button>
                    )
                )}
            </div>
        </div>
    );
}

export default EditableArousalMechanicRow;
